import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import Select from 'react-select'
import { API_URL } from '../utils/Constants/URLS'
import { X, IndianRupee, ShieldCheck, Store, MapPin, Truck, Plus, Trash2 } from 'lucide-react'
import { Row, Col, FormGroup, Label, Input, Spinner } from 'reactstrap'

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 30,
    height: 30,
    fontSize: '11px',
    borderColor: state.selectProps.hasError ? '#fb7185' : base.borderColor,
    backgroundColor: state.selectProps.hasError ? '#fff1f2' : base.backgroundColor,
    boxShadow: 'none'
  }),
  valueContainer: base => ({ ...base, padding: '0 4px', height: 28 }),
  singleValue: base => ({ ...base, margin: 0, fontSize: '11px' }),
  placeholder: base => ({ ...base, margin: 0, fontSize: '11px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }),
  input: base => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: base => ({ ...base, height: 28 }),
  dropdownIndicator: base => ({ ...base, padding: '0 4px' }),
  clearIndicator: base => ({ ...base, padding: '0 4px' }),
  menuPortal: base => ({ ...base, zIndex: 9999 })
}

const selectProps = {
  styles: selectStyles,
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed'
}

const newLineItem = () => ({
  localId: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  businessLine: '',
  productLine: '',
  model: '',
  modelMeta: null,
  quantity: 1,
  perUnitPurchasePrice: '',
  totalPurchasePrice: '',
  gst: 18,
  requiredDateOfMaterial: new Date().toISOString().split('T')[0],
  committedDeliveryDate: new Date().toISOString().split('T')[0]
})

const toModelOption = (model) => ({
  value: String(model._id || model.id),
  label: model.modelName || 'Model',
  modelData: model
})

const toProductLineOption = (productLine) => ({
  value: String(productLine._id || productLine.id),
  label: productLine.productLineName || 'Product Line',
  productLineData: productLine
})

// ─── Customer address presets ─────────────────────────────────────────────────
const CUSTOMER_ADDRESSES = {
  SILIGURI: `GREENERGY SOLUTION PVT. LTD. \nBock 'C' 1st. Floor, PCM Bus Terminus, \nSevoke Road Siliguri, \nDist-Darjeeling, West Bengal-734001`,
  KOLKATA: `GREENERGY SOLUTION PVT. LTD. \nZ-44, 39 Chak Garia, Panchasayar, \nKolkata-700094`,
  GUWAHATI: `GREENERGY SOLUTION PVT. LTD. \nGopal Boro Path, \nBy Lane 2, Gorchuk, \nGuwahati-781035`
}

const ADDRESS_EMAILS = 'partha@greenergy.net.in\nadmin@greenergy.net.in'

/** Append fixed emails + selected GSPL GSTIN to a prefilled address (idempotent). */
const withEmailsAndGstin = (address, gstin) => {
  if (!address?.trim()) return ''
  let base = address
    .replace(/\n?(?:Email:\s*)?partha@greenergy\.net\.in\s*\n(?:Email:\s*)?admin@greenergy\.net\.in(?:\nGSTIN:\s*[A-Z0-9]+)?\s*$/i, '')
    .replace(/\nGSTIN:\s*[A-Z0-9]+\s*$/i, '')
    .trimEnd()
  let out = `${base}\n${ADDRESS_EMAILS}`
  if (gstin) out += `\nGSTIN: ${gstin}`
  return out
}

const CreatePurchaseOrder = ({ isOpen, onClose, selectedDoc, onSuccess }) => {
  const token = localStorage.getItem('token')

  // Store
  const [stores, setStores] = useState([])
  const [selectedStore, setSelectedStore] = useState('')
  const [deliveryAddress, setDeliveryAddress] = useState('')

  //message
  const [message, setMessage] = useState('')
  // Customer (billing) address
  const [customerAddressPreset, setCustomerAddressPreset] = useState('')
  const [customerAddress, setCustomerAddress] = useState('')

  // GSPL GSTIN
  const [gsplGstin, setGsplGstin] = useState('')

  // Line item prices
  const [lineItemPrices, setLineItemPrices] = useState({})
  const [priceErrors, setPriceErrors] = useState({})

  // Terms & conditions
  const [terms, setTerms] = useState({
    priceValidity: '',
    paymentTerms: '',
    packing: '',
    freight: '',
    freight: '',
    transitInsurance: ''
  })

  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState({})

  // Additional line items (any business line / product line)
  const [extraLineItems, setExtraLineItems] = useState([])
  const [businessLineList, setBusinessLineList] = useState([])
  const [productLinesCache, setProductLinesCache] = useState({})
  const [modelsCache, setModelsCache] = useState({})
  const productLinesCacheRef = useRef({})
  const modelsCacheRef = useRef({})

  // Fetch all business lines once on mount
  useEffect(() => {
    const getBusinessLines = async () => {
      try {
        const res = await fetch(`${API_URL}business-lines?pagination=false`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        const result = await res.json()
        setBusinessLineList(
          (result.data?.docs || []).map(business => ({
            value: String(business.id || business._id),
            label: business.name
          }))
        )
      } catch {
        // silent – business line dropdown simply stays empty
      }
    }
    getBusinessLines()
  }, [token])

  // Fetch stores once on mount
  useEffect(() => {
    const getStoreData = async () => {
      try {
        const res = await fetch(`${API_URL}stores`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        })
        const result = await res.json()
        setStores(result.data?.docs || [])
      } catch {
        // silent – store dropdown simply stays empty
      }
    }
    getStoreData()
  }, [token])

  // Reset / pre-fill whenever the modal opens or closes
  useEffect(() => {
    if (isOpen && selectedDoc) {
      const initial = {}
      selectedDoc.lineItems?.forEach(item => {
        initial[item._id] = {
          perUnit: item.perUnitPurchasePrice || '',
          total: item.totalPurchasePrice || '',
          gst: item.gst || 18
        }
      })
      setLineItemPrices(initial)
      setPriceErrors({})
      setErrors({})
      setExtraLineItems([])
      setSelectedStore('')
      setDeliveryAddress('')
      setCustomerAddressPreset('')
      setCustomerAddress('')
      setGsplGstin('')
      setTerms({
        priceValidity: '',
        paymentTerms: '',
        packing: '',
        freight: '',
        freight: '',
        transitInsurance: ''
      })
    }
  }, [isOpen, selectedDoc])

  if (!isOpen) return null

  const vendorName = selectedDoc?.orderGivenTo?.vendor?.vendorName || 'Unknown Vendor'

  // ─── Address helpers ───────────────────────────────────────────────────────
  const handleCustomerPresetChange = (e) => {
    const preset = e.target.value
    setCustomerAddressPreset(preset)
    setCustomerAddress(preset ? withEmailsAndGstin(CUSTOMER_ADDRESSES[preset], gsplGstin) : '')
    if (errors.customerAddress) setErrors(p => ({ ...p, customerAddress: '' }))
  }

  const handleStoreChange = (e) => {
    const storeId = e.target.value
    setSelectedStore(storeId)
    const found = stores.find(s => s._id === storeId)
    setDeliveryAddress(found?.location ? withEmailsAndGstin(found.location, gsplGstin) : '')
    if (errors.store) setErrors(p => ({ ...p, store: '' }))
    if (errors.deliveryAddress) setErrors(p => ({ ...p, deliveryAddress: '' }))
  }

  const handleGsplGstinChange = (e) => {
    const nextGstin = e.target.value
    setGsplGstin(nextGstin)
    if (customerAddress?.trim()) {
      setCustomerAddress(withEmailsAndGstin(customerAddress, nextGstin))
    }
    if (deliveryAddress?.trim()) {
      setDeliveryAddress(withEmailsAndGstin(deliveryAddress, nextGstin))
    }
    if (errors.gsplGstin) setErrors(p => ({ ...p, gsplGstin: '' }))
  }

  // ─── Price helpers ────────────────────────────────────────────────────────
  const handlePerUnitChange = (itemId, perUnit, quantity) => {
    const total = perUnit !== '' ? (Number(perUnit) * quantity).toFixed(2) : ''
    setLineItemPrices(prev => ({ ...prev, [itemId]: { ...prev[itemId], perUnit, total } }))
    if (priceErrors[itemId]) setPriceErrors(prev => ({ ...prev, [itemId]: null }))
  }

  const handleGstChange = (itemId, gst) => {
    setLineItemPrices(prev => ({ ...prev, [itemId]: { ...prev[itemId], gst } }))
  }

  const handleTotalChange = (itemId, total, quantity) => {
    const perUnit = total !== '' && quantity ? (Number(total) / quantity).toFixed(2) : ''
    setLineItemPrices(prev => ({ ...prev, [itemId]: { ...prev[itemId], perUnit, total } }))
    if (priceErrors[itemId]) setPriceErrors(prev => ({ ...prev, [itemId]: null }))
  }

  const handleTermsChange = e => {
    const { name, value } = e.target
    setTerms(prev => ({ ...prev, [name]: value }))
  }

  // ─── Additional line item helpers ─────────────────────────────────────────
  const clearError = key => {
    if (errors[key]) setErrors(p => ({ ...p, [key]: '' }))
  }

  const loadProductLines = async (businessLineId) => {
    if (!businessLineId || productLinesCacheRef.current[businessLineId]) return
    try {
      const res = await fetch(
        `${API_URL}product-lines?businessLine=${businessLineId}&pagination=false`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      )
      const result = await res.json()
      productLinesCacheRef.current[businessLineId] = (result.data?.docs || []).map(toProductLineOption)
      setProductLinesCache({ ...productLinesCacheRef.current })
    } catch {
      toast.error('Failed to load product lines')
    }
  }

  const loadModels = async (businessLineId, productLineId) => {
    const cacheKey = `${businessLineId}:${productLineId}`
    if (!businessLineId || !productLineId || modelsCacheRef.current[cacheKey]) return
    try {
      const res = await fetch(
        `${API_URL}models?pagination=false&businessLine=${businessLineId}&productLine=${productLineId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          }
        }
      )
      const result = await res.json()
      const docs = result.data?.docs || result.message?.docs || result.docs || []
      modelsCacheRef.current[cacheKey] = docs.map(toModelOption)
      setModelsCache({ ...modelsCacheRef.current })
    } catch {
      toast.error('Failed to load models')
    }
  }

  const addLineItem = () => {
    setExtraLineItems(prev => [...prev, newLineItem()])
    clearError('lineItems')
  }

  const removeLineItem = (localId) => {
    setExtraLineItems(prev => prev.filter(li => li.localId !== localId))
  }

  const handleBusinessLineChange = async (localId, option) => {
    const businessLine = option?.value || ''
    setExtraLineItems(prev =>
      prev.map(li =>
        li.localId === localId
          ? { ...li, businessLine, productLine: '', model: '', modelMeta: null }
          : li
      )
    )
    clearError(`line_${localId}_businessLine`)
    if (businessLine) await loadProductLines(businessLine)
  }

  const handleProductLineChange = async (localId, businessLine, option) => {
    const productLine = option?.value || ''
    setExtraLineItems(prev =>
      prev.map(li =>
        li.localId === localId
          ? { ...li, productLine, model: '', modelMeta: null }
          : li
      )
    )
    clearError(`line_${localId}_productLine`)
    if (businessLine && productLine) await loadModels(businessLine, productLine)
  }

  const handleModelChange = (localId, option) => {
    setExtraLineItems(prev =>
      prev.map(li =>
        li.localId === localId
          ? { ...li, model: option?.value || '', modelMeta: option?.modelData || null }
          : li
      )
    )
    clearError(`line_${localId}_model`)
  }

  const updateLineItem = (localId, field, value) => {
    setExtraLineItems(prev =>
      prev.map(li => {
        if (li.localId !== localId) return li
        const updated = { ...li, [field]: value }
        const qty = Number(updated.quantity) || 0
        if (field === 'perUnitPurchasePrice') {
          updated.totalPurchasePrice = value !== '' && qty ? (Number(value) * qty).toFixed(2) : ''
        }
        if (field === 'totalPurchasePrice') {
          updated.perUnitPurchasePrice = value !== '' && qty ? (Number(value) / qty).toFixed(2) : ''
        }
        if (field === 'quantity') {
          const perUnit = Number(updated.perUnitPurchasePrice)
          if (perUnit && value) updated.totalPurchasePrice = (perUnit * Number(value)).toFixed(2)
        }
        return updated
      })
    )
    if (field === 'quantity') clearError(`line_${localId}_qty`)
    if (field === 'perUnitPurchasePrice' || field === 'totalPurchasePrice') clearError(`line_${localId}_price`)
    if (field === 'requiredDateOfMaterial') clearError(`line_${localId}_reqDate`)
    if (field === 'committedDeliveryDate') clearError(`line_${localId}_commitDate`)
  }

  // Live-compute all three PO totals from per-item GST slabs (mirrors backend logic)
  const { totalTaxableValue, totalTaxValue, totalValue } = (() => {
    let taxable = 0
    let tax = 0
      ; (selectedDoc?.lineItems || []).forEach(item => {
        const perUnit = Number(lineItemPrices[item._id]?.perUnit) || 0
        const qty = item.quantity || 0
        const gstRate = Number(lineItemPrices[item._id]?.gst) || 18
        const assessable = perUnit * qty
        const gstAmt = (assessable * gstRate) / 100
        taxable += assessable
        tax += gstAmt
      })
    extraLineItems.forEach(item => {
      const perUnit = Number(item.perUnitPurchasePrice) || 0
      const qty = Number(item.quantity) || 0
      const gstRate = Number(item.gst) || 0
      const assessable = perUnit * qty
      taxable += assessable
      tax += (assessable * gstRate) / 100
    })
    return {
      totalTaxableValue: Math.round(taxable * 100) / 100,
      totalTaxValue: Math.round(tax * 100) / 100,
      totalValue: Math.round((taxable + tax) * 100) / 100,
    }
  })()

  // ─── Validation ───────────────────────────────────────────────────────────
  const validate = () => {
    const e = {}

    if (!customerAddress?.trim()) e.customerAddress = 'Please enter billing address'
    if (!selectedStore) e.store = 'Please select a store'
    if (!deliveryAddress?.trim()) e.deliveryAddress = 'Delivery address is required'
    if (!gsplGstin) e.gsplGstin = 'Please select GSPL GSTIN'

    const newPriceErrors = {}
    selectedDoc?.lineItems?.forEach(item => {
      const val = lineItemPrices[item._id]
      // Only check if the fields are empty/undefined
      if (val?.perUnit === '' && val?.total === '') {
        newPriceErrors[item._id] = 'Enter price'
      }
    })

    const requiredTerms = ['priceValidity', 'paymentTerms', 'packing', 'freight', 'transitInsurance']
    requiredTerms.forEach(f => {
      if (!terms[f]?.trim()) e[`term_${f}`] = 'Required'
    })

    if ((selectedDoc?.lineItems?.length || 0) + extraLineItems.length === 0) {
      e.lineItems = 'At least one line item is required'
    }

    extraLineItems.forEach(li => {
      if (!li.businessLine) e[`line_${li.localId}_businessLine`] = 'Select business line'
      if (!li.productLine) e[`line_${li.localId}_productLine`] = 'Select product line'
      if (!li.model) e[`line_${li.localId}_model`] = 'Select model'
      if (!Number(li.quantity) || Number(li.quantity) <= 0) e[`line_${li.localId}_qty`] = 'Invalid qty'
      if (li.perUnitPurchasePrice === '' && li.totalPurchasePrice === '') e[`line_${li.localId}_price`] = 'Enter price'
      if (!li.requiredDateOfMaterial) e[`line_${li.localId}_reqDate`] = 'Required'
      if (!li.committedDeliveryDate) e[`line_${li.localId}_commitDate`] = 'Required'
    })

    return { ...e, ...newPriceErrors }
  }

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const validationErrors = validate()
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors)
      setPriceErrors(
        Object.fromEntries(
          Object.entries(validationErrors).filter(([k]) => !k.startsWith('term_') && !k.startsWith('line_') && k !== 'lineItems' && k !== 'store' && k !== 'customerAddress' && k !== 'deliveryAddress' && k !== 'gsplGstin')
        )
      )
      toast.warn('Please fix validation errors before submitting.')
      return
    }


    const lineItems = selectedDoc.lineItems?.map(item => ({
      model: item.model?._id || item.model,
      quantity: item.quantity,
      perUnitPurchasePrice: Number(lineItemPrices[item._id]?.perUnit) || 0,
      totalPurchasePrice: Number(lineItemPrices[item._id]?.total) || 0,
      gst: Number(lineItemPrices[item._id]?.gst) || 18,
      requiredDateOfMaterial: item.requiredDeliveryDate
        ? new Date(item.requiredDeliveryDate).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0],
      committedDeliveryDate: item.lastDateOfOrderDelivery
        ? new Date(item.lastDateOfOrderDelivery).toISOString().split('T')[0]
        : new Date().toISOString().split('T')[0]
    })) || []

    extraLineItems.forEach(li => {
      const qty = Number(li.quantity)
      const perUnit = Number(li.perUnitPurchasePrice) || 0
      lineItems.push({
        model: li.model,
        quantity: qty,
        perUnitPurchasePrice: perUnit,
        totalPurchasePrice: Number(li.totalPurchasePrice) || perUnit * qty,
        gst: Number(li.gst) || 0,
        requiredDateOfMaterial: li.requiredDateOfMaterial,
        committedDeliveryDate: li.committedDeliveryDate
      })
    })

    const payload = {
      vendor: selectedDoc.orderGivenTo.vendor._id,
      quotation: selectedDoc._id,
      store: selectedStore,
      customerAddress,
      deliveryAddress,
      gsplGstin,
      lineItems,
      ...terms
    }

    setLoading(true)
    try {
      const res = await fetch(`${API_URL}purchase-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      })
      const result = await res.json()
      if (!res.ok || result.error) {
        setMessage(result?.message)
      }
      else {
        setMessage(result?.message)
        onClose()
      }

      if (onSuccess) onSuccess()
    } catch (err) {
      toast.error(err.message || 'Something went wrong. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const inputCls = (hasErr) =>
    `w-full px-4 py-2 bg-slate-50 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm text-slate-800 ${hasErr ? 'border-rose-400 bg-rose-50 focus:ring-rose-500' : 'border-slate-200'}`

  const textareaCls = (hasErr) =>
    `w-full px-4 py-2 bg-slate-50 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all outline-none text-sm text-slate-800 resize-none ${hasErr ? 'border-rose-400 bg-rose-50 focus:ring-rose-500' : 'border-slate-200'}`

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 z-[1050] flex items-center justify-center p-4 sm:p-6 backdrop-blur-sm bg-slate-900/40">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl max-h-[95vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-white">
          <div>
            <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
              <span className="bg-green-100 text-green-600 p-1.5 rounded-lg">
                <IndianRupee size={20} />
              </span>
              Create Purchase Order
            </h2>
            <p className="text-xs text-slate-500 mt-1 font-medium m-0">
              Vendor: <span className="font-semibold text-slate-700">{vendorName}</span>
            </p>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-slate-100 rounded-full text-slate-500 transition-colors border-0 bg-transparent">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6" style={{ maxHeight: 'calc(95vh - 140px)' }}>

          {/* Address Section */}
          <div className="bg-slate-50 p-5 rounded-xl border border-slate-200 mb-5">
            <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
              GSPL GSTIN <span className="text-rose-500">*</span>
              {errors.gsplGstin && <span className="ml-2 text-xs font-normal text-rose-500">{errors.gsplGstin}</span>}
            </label>
            <select
              className={`${inputCls(!!errors.gsplGstin)} w-full md:w-1/2`}
              value={gsplGstin}
              onChange={handleGsplGstinChange}
            >
              <option value=''>-- Select GSPL GSTIN --</option>
              <option value='19AAECG1743K1ZG'>19AAECG1743K1ZG</option>
              <option value='18AAECG1743K1ZI'>18AAECG1743K1ZI</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">

            {/* ── Billing / Customer Address ── */}
            <div className="bg-blue-50 p-5 rounded-xl border border-blue-100">
              <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <MapPin size={15} className="text-blue-500" /> Billing Address (Customer)
                <span className="text-rose-500">*</span>
                {errors.customerAddress && <span className="ml-2 text-xs font-normal text-rose-500">{errors.customerAddress}</span>}
              </label>

              {/* Preset Dropdown */}
              <select
                className={`${inputCls(!!errors.customerAddress)} mb-3`}
                value={customerAddressPreset}
                onChange={handleCustomerPresetChange}
              >
                <option value="">-- Select Office Location --</option>
                <option value="SILIGURI">Siliguri</option>
                <option value="KOLKATA">Kolkata</option>
                <option value="GUWAHATI">Guwahati</option>
              </select>

              {/* Editable Textarea */}
              <textarea
                rows={7}
                className={textareaCls(!!errors.customerAddress)}
                value={customerAddress}
                onChange={e => {
                  setCustomerAddress(e.target.value)
                  if (errors.customerAddress) setErrors(p => ({ ...p, customerAddress: '' }))
                }}
                placeholder="Select a location above or type the billing address..."
              />
            </div>

            {/* ── Delivery / Shipping Address ── */}
            <div className="bg-green-50 p-5 rounded-xl border border-green-100">
              <label className="block text-sm font-bold text-slate-700 mb-2 flex items-center gap-2">
                <Truck size={15} className="text-green-600" /> Shipping Address (Delivery)
                <span className="text-rose-500">*</span>
                {errors.deliveryAddress && <span className="ml-2 text-xs font-normal text-rose-500">{errors.deliveryAddress}</span>}
              </label>

              {/* Store Selector */}
              <select
                className={`${inputCls(!!errors.store)} mb-3`}
                value={selectedStore}
                onChange={handleStoreChange}
              >
                <option value="">-- Select Store --</option>
                {stores.map(s => (
                  <option key={s?._id} value={s?._id}>{s?.name}</option>
                ))}
              </select>
              {errors.store && <p className="text-xs text-rose-500 -mt-2 mb-2">{errors.store}</p>}

              {/* Editable Textarea pre-filled with store's location */}
              <textarea
                rows={7}
                className={textareaCls(!!errors.deliveryAddress)}
                value={deliveryAddress}
                onChange={e => {
                  setDeliveryAddress(e.target.value)
                  if (errors.deliveryAddress) setErrors(p => ({ ...p, deliveryAddress: '' }))
                }}
                placeholder="Select a store above — address will be pre-filled automatically..."
              />
            </div>
          </div>

          {/* Line Items: per-unit & total price */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-0 flex items-center gap-2">
                <IndianRupee size={13} /> Line Item Prices
              </h3>
              <button
                type="button"
                onClick={addLineItem}
                className="flex items-center gap-1.5 text-xs font-bold text-blue-600 bg-blue-50 border border-blue-200 px-3 py-1.5 rounded-lg hover:bg-blue-100 transition-colors"
              >
                <Plus size={14} /> Add Line Item
              </button>
            </div>
            {errors.lineItems && <p className="text-xs text-rose-500 mb-2">{errors.lineItems}</p>}
            <div className="rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left align-middle">
                  <thead className="bg-gray-900 text-gray-400 text-xs uppercase tracking-widest">
                    <tr>
                      {['#', 'Model', 'UOM', 'Qty', 'Per Unit Price (₹)', 'GST %', 'Total Price (₹)'].map(h => (
                        <th key={h} className="px-4 py-3 font-semibold">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 bg-white">
                    {selectedDoc?.lineItems?.map((item, idx) => (
                      <tr key={item._id} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-slate-400 font-semibold">{idx + 1}</td>
                        <td className="px-4 py-3">
                          <p className="font-bold text-slate-800 mb-0">{item.model?.modelName}</p>
                          <p className="text-xs text-slate-400 font-mono mb-0">{item.model?.partCode}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className="bg-slate-100 border border-slate-200 text-slate-600 text-xs font-semibold px-2 py-1 rounded-md uppercase">
                            {item.model?.uom}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-bold text-slate-800">{item.quantity}</td>
                        <td className="px-4 py-3">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹/u</span>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={lineItemPrices[item._id]?.perUnit ?? ''}
                              onChange={e => handlePerUnitChange(item._id, e.target.value, item.quantity)}
                              className={`w-36 pl-10 pr-3 py-2 text-sm border rounded-lg outline-none ${priceErrors[item._id] ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                            />
                          </div>
                          {priceErrors[item._id] && <p className="text-xs text-rose-500 mt-0.5 mb-0">{priceErrors[item._id]}</p>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">%</span>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.1"
                              value={lineItemPrices[item._id]?.gst ?? 18}
                              onChange={e => handleGstChange(item._id, e.target.value)}
                              className="w-24 pl-8 pr-3 py-2 text-sm border border-gray-300 rounded-lg outline-none"
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">₹tot</span>
                            <input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={lineItemPrices[item._id]?.total ?? ''}
                              onChange={e => handleTotalChange(item._id, e.target.value, item.quantity)}
                              className="w-36 pl-12 pr-3 py-2 text-sm border border-gray-300 rounded-lg outline-none"
                            />
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {extraLineItems.length > 0 && (
                <div className="border-t-2 border-slate-200">
                  <p className="px-4 py-2 mb-0 text-[10px] font-bold text-slate-500 uppercase tracking-widest bg-slate-50">
                    Additional Line Items
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm text-left align-middle">
                      <thead className="bg-gray-900 text-gray-400 text-xs uppercase tracking-widest">
                        <tr>
                          {['#', 'Business Line', 'Product Line', 'Model', 'Part Code', 'UOM', 'Qty', 'Per Unit (₹)', 'GST %', 'Total (₹)', 'Required Date', 'Committed Date', ''].map(h => (
                            <th key={h} className="px-2 py-2 font-semibold whitespace-nowrap">{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100 bg-white">
                        {extraLineItems.map((item, idx) => {
                          const productLineOptions = item.businessLine ? productLinesCache[item.businessLine] || [] : []
                          const modelOptions = item.businessLine && item.productLine
                            ? modelsCache[`${item.businessLine}:${item.productLine}`] || []
                            : []
                          const modelDetails = item.modelMeta

                          return (
                            <tr key={item.localId} className="hover:bg-slate-50 align-middle">
                              <td className="px-2 py-1.5 text-slate-400">{(selectedDoc?.lineItems?.length || 0) + idx + 1}</td>
                              <td className="px-2 py-1.5 min-w-[130px]">
                                <Select
                                  {...selectProps}
                                  options={businessLineList}
                                  placeholder="business line"
                                  isClearable
                                  hasError={!!errors[`line_${item.localId}_businessLine`]}
                                  value={businessLineList.find(o => o.value === item.businessLine) || null}
                                  onChange={option => handleBusinessLineChange(item.localId, option)}
                                />
                              </td>
                              <td className="px-2 py-1.5 min-w-[130px]">
                                <Select
                                  {...selectProps}
                                  options={productLineOptions}
                                  placeholder="product line"
                                  isClearable
                                  isDisabled={!item.businessLine}
                                  hasError={!!errors[`line_${item.localId}_productLine`]}
                                  value={productLineOptions.find(o => o.value === item.productLine) || null}
                                  onChange={option => handleProductLineChange(item.localId, item.businessLine, option)}
                                />
                              </td>
                              <td className="px-2 py-1.5 min-w-[160px]">
                                <Select
                                  {...selectProps}
                                  options={modelOptions}
                                  placeholder="model"
                                  isClearable
                                  isDisabled={!item.productLine}
                                  hasError={!!errors[`line_${item.localId}_model`]}
                                  value={modelOptions.find(o => o.value === String(item.model)) || null}
                                  onChange={option => handleModelChange(item.localId, option)}
                                />
                              </td>
                              <td className="px-2 py-1.5 text-xs font-mono text-slate-600 whitespace-nowrap">
                                {modelDetails?.partCode || '—'}
                              </td>
                              <td className="px-2 py-1.5 text-xs text-slate-600 whitespace-nowrap">
                                {modelDetails?.uom || '—'}
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="number"
                                  min="1"
                                  value={item.quantity}
                                  onChange={e => updateLineItem(item.localId, 'quantity', e.target.value)}
                                  className={`w-20 px-2 py-1.5 text-sm border rounded-lg ${errors[`line_${item.localId}_qty`] ? 'border-rose-400' : 'border-gray-300'}`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.perUnitPurchasePrice}
                                  onChange={e => updateLineItem(item.localId, 'perUnitPurchasePrice', e.target.value)}
                                  className={`w-28 px-2 py-1.5 text-sm border rounded-lg ${errors[`line_${item.localId}_price`] ? 'border-rose-400 bg-red-50' : 'border-gray-300'}`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <select
                                  className="w-20 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                                  value={item.gst}
                                  onChange={e => updateLineItem(item.localId, 'gst', e.target.value)}
                                >
                                  <option value={0.1}>0.1</option>
                                  <option value={5}>5</option>
                                  <option value={12}>12</option>
                                  <option value={18}>18</option>
                                  <option value={28}>28</option>
                                </select>
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  value={item.totalPurchasePrice}
                                  onChange={e => updateLineItem(item.localId, 'totalPurchasePrice', e.target.value)}
                                  className="w-28 px-2 py-1.5 text-sm border border-gray-300 rounded-lg"
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="date"
                                  value={item.requiredDateOfMaterial}
                                  onChange={e => updateLineItem(item.localId, 'requiredDateOfMaterial', e.target.value)}
                                  className={`px-2 py-1.5 text-sm border rounded-lg ${errors[`line_${item.localId}_reqDate`] ? 'border-rose-400' : 'border-gray-300'}`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <input
                                  type="date"
                                  value={item.committedDeliveryDate}
                                  onChange={e => updateLineItem(item.localId, 'committedDeliveryDate', e.target.value)}
                                  className={`px-2 py-1.5 text-sm border rounded-lg ${errors[`line_${item.localId}_commitDate`] ? 'border-rose-400' : 'border-gray-300'}`}
                                />
                              </td>
                              <td className="px-2 py-1.5">
                                <button
                                  type="button"
                                  onClick={() => removeLineItem(item.localId)}
                                  className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg border-0 bg-transparent"
                                  title="Remove line item"
                                >
                                  <Trash2 size={16} />
                                </button>
                              </td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="bg-slate-800 border-t-2 border-slate-700 px-6 py-4">
                <div className="flex flex-wrap items-center justify-end gap-6">
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Taxable Value</p>
                    <p className="text-base font-bold text-slate-200 mb-0">₹ {totalTaxableValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-0.5">Total Tax (GST)</p>
                    <p className="text-base font-bold text-amber-400 mb-0">₹ {totalTaxValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                  <div className="text-right border-l border-slate-600 pl-6">
                    <p className="text-[10px] font-bold text-green-400 uppercase tracking-widest mb-0.5">Grand Total</p>
                    <p className="text-2xl font-extrabold text-green-400 mb-0">₹ {totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</p>
                  </div>
                </div>
              </div>
            </div>

          </div>

          {/* Terms & Conditions */}
          <div className="bg-white p-5 rounded-xl border border-slate-100">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest mb-4 flex items-center gap-2">
              <ShieldCheck size={14} /> Terms & Conditions <span className="text-rose-400">(required)</span>
            </h3>
            <Row>
              {[
                { label: 'Price Basis', name: 'priceValidity', placeholder: 'e.g. 30 Days' },
                { label: 'Payment Terms', name: 'paymentTerms', placeholder: 'e.g. 50% Advance' },
                { label: 'Packing', name: 'packing', placeholder: 'e.g. Standard Packing' },
                { label: 'Freight', name: 'freight', placeholder: 'e.g. Extra at Actuals' },
                { label: 'Transit Insurance', name: 'transitInsurance', placeholder: 'e.g. By Vendor' }
              ].map(f => (
                <Col md="4" key={f.name} className="mb-3">
                  <FormGroup>
                    <Label className="text-xs font-bold text-gray-600 mb-1">
                      {f.label} <span className="text-rose-400">*</span>
                      {errors[`term_${f.name}`] && <span className="ml-1 font-normal text-rose-500">{errors[`term_${f.name}`]}</span>}
                    </Label>
                    <Input
                      name={f.name}
                      value={terms[f.name]}
                      onChange={handleTermsChange}
                      placeholder={f.placeholder}
                      className={`text-sm ${errors[`term_${f.name}`] ? 'border-rose-400' : 'border-gray-300'}`}
                    />
                  </FormGroup>
                </Col>
              ))}
            </Row>

            <hr className="my-4" />
          </div>

        </div>
        {
          message && (
            <div className='p-6 mt-4'>
              <span className='bg-blue-100 p-3 rounded-xl border border-slate-100 text-blue-600'>{message}</span>
            </div>
          )
        }


        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="px-5 py-2.5 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={loading}
            className="px-5 py-2.5 text-sm font-bold text-white bg-green-600 border-0 rounded-lg hover:bg-green-700 transition-all flex items-center gap-2 shadow-sm"
          >
            {loading ? (
              <><Spinner size="sm" className="text-white" /> Creating...</>
            ) : (
              <><IndianRupee size={15} /> Confirm & Create PO</>
            )}
          </button>
        </div>

      </div>
    </div>
  )
}

export default CreatePurchaseOrder
